/**
 * Blob & Wave Generator - Utility für organische SVG-Formen
 *
 * Generiert:
 * - Blobs: Organische, amöbenartige Formen mit Bezier-Kurven
 * - Waves: Wellenlinien für Hintergründe und Trennelemente
 *
 * Alle Funktionen verwenden deterministische Seeds für Reproduzierbarkeit.
 */

// ============================================================================
// TYPEN
// ============================================================================

/**
 * Konfiguration für Blob-Generierung
 */
export interface BlobConfig {
  /** Komplexität der Form (3-10 Punkte) */
  complexity: number;
  /** Kontrast/Unregelmäßigkeit (0-1, wobei 0 = Kreis, 1 = sehr unregelmäßig) */
  contrast: number;
  /** Seed für deterministische Generierung */
  seed: number;
  /** Größe des ViewBox (Standard: 100) */
  size?: number;
}

/**
 * Konfiguration für Wellen-Generierung
 */
export interface WaveConfig {
  /** Amplitude der Welle (Höhe der Auslenkung) */
  amplitude: number;
  /** Frequenz (Anzahl der Wellen) */
  frequency: number;
  /** Phasenverschiebung (0-2*PI) */
  phase: number;
  /** Breite des SVG (Standard: 100) */
  width?: number;
  /** Höhe des SVG (Standard: 100) */
  height?: number;
  /** Seed für leichte Variationen */
  seed?: number;
  /** Art der Welle */
  waveType?: 'sine' | 'smooth' | 'sharp' | 'organic';
}

/**
 * Punkt im 2D-Raum
 */
interface Point {
  x: number;
  y: number;
}

// ============================================================================
// ZUFALLSGENERATOR MIT SEED
// ============================================================================

/**
 * Einfacher Seeded Random Number Generator (Mulberry32)
 * Ermöglicht reproduzierbare Zufallszahlen basierend auf einem Seed
 */
function seededRandom(seed: number): () => number {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/**
 * Generiert einen zufälligen Seed-Wert
 * Nützlich für erste Generierung oder "Würfeln" neuer Formen
 */
export function getRandomSeed(): number {
  return Math.floor(Math.random() * 1000000);
}

// ============================================================================
// BLOB GENERATOR
// ============================================================================

/**
 * Generiert einen SVG-Pfad für eine organische Blob-Form
 *
 * Algorithmus:
 * 1. Punkte gleichmäßig auf einem Kreis verteilen
 * 2. Radius jedes Punktes basierend auf Seed und Kontrast variieren
 * 3. Smooth Bezier-Kurven zwischen den Punkten generieren
 *
 * @param config - Konfigurationsparameter für den Blob
 * @returns SVG path d-Attribut String
 */
export function generateBlobPath(config: BlobConfig): string {
  const {
    complexity,
    contrast,
    seed,
    size = 100
  } = config;

  // Validierung
  const numPoints = Math.max(3, Math.min(10, Math.round(complexity)));
  const normalizedContrast = Math.max(0, Math.min(1, contrast));

  // Seeded Random für Reproduzierbarkeit
  const random = seededRandom(seed);

  // Zentrum und Basis-Radius
  const center = size / 2;
  const baseRadius = size * 0.35; // 35% der Größe als Basis
  const maxVariation = baseRadius * normalizedContrast * 0.5; // Max. 50% Variation bei contrast=1

  // Punkte auf dem Kreis generieren mit variierten Radien
  const points: Point[] = [];
  const angleStep = (Math.PI * 2) / numPoints;

  for (let i = 0; i < numPoints; i++) {
    const angle = i * angleStep - Math.PI / 2; // Start oben
    // Radius variieren basierend auf Random und Kontrast
    const radiusVariation = (random() - 0.5) * 2 * maxVariation;
    const radius = baseRadius + radiusVariation;

    points.push({
      x: center + Math.cos(angle) * radius,
      y: center + Math.sin(angle) * radius
    });
  }

  // Bezier-Kurven zwischen Punkten erstellen (Catmull-Rom zu Cubic Bezier)
  return createSmoothPath(points, true);
}

/**
 * Erstellt einen glatten SVG-Pfad durch die gegebenen Punkte
 * Verwendet kubische Bezier-Kurven für natürliche Übergänge
 *
 * @param points - Array von Punkten
 * @param closed - Ob der Pfad geschlossen werden soll
 * @returns SVG path d-Attribut String
 */
function createSmoothPath(points: Point[], closed: boolean): string {
  if (points.length < 2) return '';

  const tension = 0.3; // Kontrolliert die "Straffheit" der Kurven

  // Für geschlossene Pfade: Punkte am Anfang und Ende duplizieren
  const workingPoints = closed
    ? [points[points.length - 1], ...points, points[0], points[1]]
    : points;

  const path: string[] = [];

  // Startpunkt
  if (closed) {
    path.push(`M ${points[0].x.toFixed(2)},${points[0].y.toFixed(2)}`);
  } else {
    path.push(`M ${workingPoints[0].x.toFixed(2)},${workingPoints[0].y.toFixed(2)}`);
  }

  // Kubische Bezier-Kurven durch alle Punkte
  for (let i = 1; i < workingPoints.length - 2; i++) {
    const p0 = workingPoints[i - 1];
    const p1 = workingPoints[i];
    const p2 = workingPoints[i + 1];
    const p3 = workingPoints[i + 2];

    // Kontrollpunkte berechnen (basierend auf Catmull-Rom Spline)
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    path.push(`C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`);
  }

  if (closed) {
    path.push('Z');
  }

  return path.join(' ');
}

/**
 * Generiert ein komplettes SVG-Element für einen Blob
 *
 * @param config - Blob-Konfiguration
 * @param fill - Füllfarbe (Standard: currentColor)
 * @returns Komplettes SVG als String
 */
export function generateBlobSvg(config: BlobConfig, fill: string = 'currentColor'): string {
  const size = config.size || 100;
  const path = generateBlobPath(config);

  return `<svg viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
    <path d="${path}" fill="${fill}"/>
  </svg>`;
}

// ============================================================================
// WAVE GENERATOR
// ============================================================================

/**
 * Generiert einen SVG-Pfad für eine Wellenlinie
 *
 * @param config - Konfigurationsparameter für die Welle
 * @returns SVG path d-Attribut String
 */
export function generateWavePath(config: WaveConfig): string {
  const {
    amplitude,
    frequency,
    phase,
    width = 100,
    height = 100,
    seed = 0,
    waveType = 'sine'
  } = config;

  const random = seed ? seededRandom(seed) : () => 0;

  // Anzahl der Segmente für glatte Kurve
  const segments = Math.max(20, frequency * 10);
  const segmentWidth = width / segments;

  // Mittlere Y-Position
  const midY = height / 2;

  // Punkte auf der Welle berechnen
  const points: Point[] = [];

  for (let i = 0; i <= segments; i++) {
    const x = i * segmentWidth;
    const t = (i / segments) * Math.PI * 2 * frequency + phase;

    let y: number;
    switch (waveType) {
      case 'sine':
        // Standard-Sinuswelle
        y = midY + Math.sin(t) * amplitude;
        break;

      case 'smooth':
        // Weichere Welle mit höheren Harmonischen
        y = midY + (Math.sin(t) * 0.6 + Math.sin(t * 2) * 0.3 + Math.sin(t * 3) * 0.1) * amplitude;
        break;

      case 'sharp':
        // Dreieckswelle (schärfere Spitzen)
        const normalized = ((t % (Math.PI * 2)) / (Math.PI * 2));
        y = midY + (normalized < 0.5 ? normalized * 4 - 1 : 3 - normalized * 4) * amplitude;
        break;

      case 'organic':
        // Organische Welle mit Zufallsvariationen
        const baseWave = Math.sin(t) * 0.7 + Math.sin(t * 1.5 + 1) * 0.3;
        const noise = (random() - 0.5) * 0.2;
        y = midY + (baseWave + noise) * amplitude;
        break;

      default:
        y = midY + Math.sin(t) * amplitude;
    }

    points.push({ x, y });
  }

  // Pfad als Bezier-Kurven erstellen
  return createSmoothPath(points, false);
}

/**
 * Generiert einen geschlossenen Wellen-Pfad (für gefüllte Bereiche)
 * Die Welle wird unten mit einer geraden Linie geschlossen
 *
 * @param config - Wellen-Konfiguration
 * @param fillDirection - 'top' oder 'bottom' (wo die Füllung sein soll)
 * @returns SVG path d-Attribut String
 */
export function generateFilledWavePath(
  config: WaveConfig,
  fillDirection: 'top' | 'bottom' = 'bottom'
): string {
  const { width = 100, height = 100 } = config;
  const wavePath = generateWavePath(config);

  if (fillDirection === 'bottom') {
    // Welle oben, Füllung unten
    return `${wavePath} L ${width},${height} L 0,${height} Z`;
  } else {
    // Welle unten, Füllung oben
    return `${wavePath} L ${width},0 L 0,0 Z`;
  }
}

/**
 * Generiert ein komplettes SVG-Element für eine Welle
 *
 * @param config - Wellen-Konfiguration
 * @param fill - Füllfarbe (Standard: currentColor)
 * @param filled - Ob die Welle gefüllt sein soll
 * @param strokeWidth - Linienstärke (nur wenn nicht gefüllt)
 * @returns Komplettes SVG als String
 */
export function generateWaveSvg(
  config: WaveConfig,
  fill: string = 'currentColor',
  filled: boolean = true,
  strokeWidth: number = 2
): string {
  const width = config.width || 100;
  const height = config.height || 100;

  const path = filled
    ? generateFilledWavePath(config)
    : generateWavePath(config);

  const pathAttrs = filled
    ? `fill="${fill}"`
    : `fill="none" stroke="${fill}" stroke-width="${strokeWidth}" stroke-linecap="round"`;

  return `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
    <path d="${path}" ${pathAttrs}/>
  </svg>`;
}

// ============================================================================
// MEHRSCHICHTIGE WELLEN
// ============================================================================

/**
 * Konfiguration für mehrschichtige Wellen
 */
export interface LayeredWaveConfig {
  /** Anzahl der Schichten */
  layers: number;
  /** Basis-Konfiguration für alle Wellen */
  baseConfig: Omit<WaveConfig, 'phase'>;
  /** Farben für jede Schicht (von hinten nach vorne) */
  colors: string[];
  /** Opazität pro Schicht (optional) */
  opacities?: number[];
}

/**
 * Generiert mehrere überlagerte Wellenebenen
 * Ideal für Hintergrund-Effekte
 *
 * @param config - Konfiguration für mehrschichtige Wellen
 * @returns Array von SVG-Pfaden mit Styles
 */
export function generateLayeredWaves(config: LayeredWaveConfig): Array<{
  path: string;
  fill: string;
  opacity: number;
}> {
  const { layers, baseConfig, colors, opacities = [] } = config;
  const result: Array<{ path: string; fill: string; opacity: number }> = [];

  for (let i = 0; i < layers; i++) {
    // Phase und Amplitude pro Schicht leicht variieren
    const phaseOffset = (i / layers) * Math.PI;
    const amplitudeScale = 1 - (i / layers) * 0.3; // Vordere Schichten kleiner

    const waveConfig: WaveConfig = {
      ...baseConfig,
      phase: phaseOffset,
      amplitude: baseConfig.amplitude * amplitudeScale,
      seed: (baseConfig.seed || 0) + i * 1000
    };

    result.push({
      path: generateFilledWavePath(waveConfig),
      fill: colors[i % colors.length],
      opacity: opacities[i] ?? (1 - i * 0.2)
    });
  }

  return result;
}

/**
 * Generiert ein komplettes mehrschichtiges Wellen-SVG
 *
 * @param config - Konfiguration
 * @returns Komplettes SVG als String
 */
export function generateLayeredWavesSvg(config: LayeredWaveConfig): string {
  const layers = generateLayeredWaves(config);
  const width = config.baseConfig.width || 100;
  const height = config.baseConfig.height || 100;

  const paths = layers
    .map(layer => `<path d="${layer.path}" fill="${layer.fill}" opacity="${layer.opacity}"/>`)
    .join('\n    ');

  return `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
    ${paths}
  </svg>`;
}

// ============================================================================
// PRESET-KONFIGURATIONEN
// ============================================================================

/**
 * Vordefinierte Blob-Konfigurationen für schnelle Verwendung
 */
export const BLOB_PRESETS = {
  /** Einfacher, runder Blob */
  simple: { complexity: 4, contrast: 0.3 },
  /** Mittelkomplexer Blob */
  medium: { complexity: 6, contrast: 0.5 },
  /** Komplexer, unregelmäßiger Blob */
  complex: { complexity: 8, contrast: 0.7 },
  /** Sehr organischer Blob */
  organic: { complexity: 10, contrast: 0.8 },
  /** Fast kreisrund */
  smooth: { complexity: 5, contrast: 0.15 },
} as const;

/**
 * Vordefinierte Wellen-Konfigurationen
 */
export const WAVE_PRESETS = {
  /** Sanfte Welle */
  gentle: { amplitude: 10, frequency: 2, phase: 0, waveType: 'sine' as const },
  /** Starke Welle */
  strong: { amplitude: 25, frequency: 3, phase: 0, waveType: 'sine' as const },
  /** Organische Welle */
  organic: { amplitude: 15, frequency: 2.5, phase: 0, waveType: 'organic' as const },
  /** Scharfe Welle */
  sharp: { amplitude: 20, frequency: 4, phase: 0, waveType: 'sharp' as const },
  /** Sehr flache Welle */
  subtle: { amplitude: 5, frequency: 1.5, phase: 0, waveType: 'smooth' as const },
} as const;

// ============================================================================
// HILFSFUNKTIONEN
// ============================================================================

/**
 * Kombiniert mehrere Blob-Pfade zu einem komplexeren Shape
 *
 * @param configs - Array von Blob-Konfigurationen
 * @returns Kombinierter SVG-Pfad String
 */
export function combineBlobs(configs: BlobConfig[]): string {
  return configs.map(config => generateBlobPath(config)).join(' ');
}

/**
 * Erstellt einen animierbaren Blob mit zwei Zuständen
 * Nützlich für CSS/SMIL Animationen
 *
 * @param seed - Basis-Seed
 * @param complexity - Komplexität
 * @param contrast - Kontrast
 * @returns Objekt mit zwei Pfaden für Animation
 */
export function createAnimatableBlob(
  seed: number,
  complexity: number,
  contrast: number
): { from: string; to: string } {
  return {
    from: generateBlobPath({ complexity, contrast, seed }),
    to: generateBlobPath({ complexity, contrast, seed: seed + 12345 })
  };
}

/**
 * Validiert und normalisiert eine Blob-Konfiguration
 *
 * @param config - Eingabe-Konfiguration
 * @returns Normalisierte Konfiguration
 */
export function normalizeBlobConfig(config: Partial<BlobConfig>): BlobConfig {
  return {
    complexity: Math.max(3, Math.min(10, config.complexity ?? 6)),
    contrast: Math.max(0, Math.min(1, config.contrast ?? 0.5)),
    seed: config.seed ?? getRandomSeed(),
    size: config.size ?? 100
  };
}

/**
 * Validiert und normalisiert eine Wellen-Konfiguration
 *
 * @param config - Eingabe-Konfiguration
 * @returns Normalisierte Konfiguration
 */
export function normalizeWaveConfig(config: Partial<WaveConfig>): WaveConfig {
  return {
    amplitude: Math.max(1, Math.min(50, config.amplitude ?? 15)),
    frequency: Math.max(0.5, Math.min(10, config.frequency ?? 2)),
    phase: config.phase ?? 0,
    width: config.width ?? 100,
    height: config.height ?? 100,
    seed: config.seed ?? 0,
    waveType: config.waveType ?? 'sine'
  };
}
